/**
 * workstationSvc — a `svc=` service base is honoured only when it names a
 * workstation the signed-in person can already reach (S1b-3a, 2026-09-26).
 *
 * `/desktop?svc=<base>` (and a run card's `svc`) point the page's browser-
 * service calls at the machine that hosts a session. The rule until
 * 2026-09-26 was a SHAPE: https, a dashed-IP `*.sslip.io` host, a known path.
 * sslip.io resolves ANY address, so the shape admitted every machine on the
 * internet. With the base pointed there, the sign-in window and the in-chat
 * live view would draw that machine's "desktop" — somewhere a person types an
 * app password believing it is Prest0n's browser — and, until the same change
 * in baseUrl.ts, send it the internal key or the person's ticket too.
 *
 * Now the shape is necessary and not sufficient: the host must also be one of
 * the person's own workstations — the platform-shared host as the platform
 * names it (Firestore), or a machine recorded under the workspace they are in
 * (`accounts/{id}/vm/*`, which firestore.rules opens to exactly its verified
 * members). A host that is neither is ignored, and the page keeps the base it
 * would have used without the parameter.
 */
import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { collection, getDocs, getFirestore } from 'firebase/firestore';
import { resolveAccount } from '../prest0nRuntime/accountResolver';
import { ACCOUNT_FIRESTORE_PREFIX } from '../prest0nRuntime/constants';
import { isSafeAccountVmBase } from '../../hooks/realtimeDesktopUrl';
import { loadCanonicalSharedBrowserServiceBase } from './baseUrl';

const DASHED_SSLIP_HOST = /^(?:\d{1,3}-){3}\d{1,3}\.sslip\.io$/;
const SVC_PATHS: readonly string[] = ['', '/browser', '/browser-api'];

/**
 * The shape `svc=` has always required — https, one of our dashed-sslip
 * hosts, a known service path, no credentials — normalized to `origin+path`.
 * null for anything else. Shape only: see vetWorkstationSvc for WHOSE machine.
 */
export function parseWorkstationSvc(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const u = new URL(raw.trim());
    if (u.protocol !== 'https:' || u.username || u.password || u.port) return null;
    if (!DASHED_SSLIP_HOST.test(u.hostname)) return null;
    if (u.search || u.hash) return null;
    const path = u.pathname.replace(/\/+$/, '');
    if (!SVC_PATHS.includes(path)) return null;
    return `${u.origin}${path}`;
  } catch {
    return null;
  }
}

/** The https origin of a base, or null. */
export function workstationOriginOf(base: unknown): string | null {
  if (typeof base !== 'string' || !base.trim()) return null;
  try {
    const u = new URL(base.trim());
    return u.protocol === 'https:' && !u.username && !u.password ? u.origin : null;
  } catch {
    return null;
  }
}

/**
 * Pure: the origins a `svc=` may name, from the bases the person can reach.
 * Each base must pass the same gate the desktop target does
 * (isSafeAccountVmBase); anything else contributes nothing.
 */
export function knownWorkstationOriginsFrom(bases: readonly unknown[]): Set<string> {
  const out = new Set<string>();
  for (const base of bases) {
    if (typeof base !== 'string' || !isSafeAccountVmBase(base.trim())) continue;
    const origin = workstationOriginOf(base);
    if (origin) out.add(origin);
  }
  return out;
}

/**
 * The origins this person's `svc=` may name: the platform-shared host as the
 * platform names it, and every workstation recorded under the workspace they
 * are in. A read that fails contributes nothing — the parameter is then
 * ignored, which is the safe answer.
 */
export async function readKnownWorkstationOrigins(): Promise<Set<string>> {
  const bases: unknown[] = [await loadCanonicalSharedBrowserServiceBase().catch(() => null)];
  try {
    const user = getAuth().currentUser;
    const accountId = user ? (await resolveAccount(user.uid))?.accountId : null;
    if (accountId) {
      const snap = await getDocs(collection(getFirestore(getApp()), ACCOUNT_FIRESTORE_PREFIX, accountId, 'vm'));
      snap.forEach((d) => {
        bases.push((d.data() as { baseUrl?: unknown } | undefined)?.baseUrl);
      });
    }
  } catch {
    /* nothing more is known — the shared host alone */
  }
  return knownWorkstationOriginsFrom(bases);
}

/**
 * The base to honour for a `svc=` value, or null when it must be ignored: the
 * shape must hold AND its host must be one of the person's own workstations.
 */
export async function vetWorkstationSvc(
  raw: unknown,
  readKnown: () => Promise<Set<string>> = readKnownWorkstationOrigins,
): Promise<string | null> {
  const parsed = parseWorkstationSvc(raw);
  if (!parsed) return null;
  const origin = workstationOriginOf(parsed);
  if (!origin) return null;
  const known = await readKnown().catch(() => new Set<string>());
  return known.has(origin) ? parsed : null;
}
